import { db } from '../firebase'
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'

// TypeScript interfaces
export interface Alert {
  id?: string
  studentId: string
  studentName: string
  reason: string
  practiceHours?: number
  details?: string
  status: 'active' | 'dismissed'
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

export interface AlertData {
  studentId: string
  studentName: string
  reason: string
  practiceHours?: number
  details?: string
}

/**
 * Create a new alert for a student
 */
export const createAlert = async (
  teacherId: string,
  alertData: AlertData,
): Promise<string> => {
  try {
    console.log('Creating alert for teacher:', teacherId, alertData)

    if (!teacherId || !alertData.studentId || !alertData.studentName) {
      throw new Error(
        'Missing required fields: teacherId, studentId, or studentName',
      )
    }

    const alertsRef = collection(db, 'teachers', teacherId, 'alerts')

    const dataToSave = {
      ...alertData,
      status: 'active' as const,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const newAlertRef = await addDoc(alertsRef, dataToSave)
    console.log('Alert created with ID:', newAlertRef.id)

    return newAlertRef.id
  } catch (error) {
    console.error('Error creating alert:', error)
    throw error
  }
}

/**
 * Get all active alerts for a teacher
 */
export const getActiveAlerts = async (teacherId: string): Promise<Alert[]> => {
  try {
    console.log('Fetching active alerts for teacher:', teacherId)

    if (!teacherId) {
      throw new Error('Teacher ID is required')
    }

    const alertsRef = collection(db, 'teachers', teacherId, 'alerts')
    const alertsQuery = query(
      alertsRef,
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
    )

    const alertsSnapshot = await getDocs(alertsQuery)

    const alerts: Alert[] = alertsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Alert,
    )

    console.log(`Found ${alerts.length} active alerts`)
    return alerts
  } catch (error) {
    console.error('Error fetching active alerts:', error)
    throw error
  }
}

/**
 * Get all alerts (active and dismissed) for a teacher
 */
export const getAllAlerts = async (teacherId: string): Promise<Alert[]> => {
  try {
    console.log('Fetching all alerts for teacher:', teacherId)

    if (!teacherId) {
      throw new Error('Teacher ID is required')
    }

    const alertsRef = collection(db, 'teachers', teacherId, 'alerts')
    const alertsQuery = query(alertsRef, orderBy('createdAt', 'desc'))

    const alertsSnapshot = await getDocs(alertsQuery)

    const alerts: Alert[] = alertsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Alert,
    )

    console.log(`Found ${alerts.length} total alerts`)
    return alerts
  } catch (error) {
    console.error('Error fetching all alerts:', error)
    throw error
  }
}

/**
 * Dismiss an alert (mark as dismissed)
 */
export const dismissAlert = async (
  teacherId: string,
  alertId: string,
): Promise<boolean> => {
  try {
    console.log('Dismissing alert:', alertId)

    if (!teacherId || !alertId) {
      throw new Error('Teacher ID and Alert ID are required')
    }

    const alertRef = doc(db, 'teachers', teacherId, 'alerts', alertId)

    await updateDoc(alertRef, {
      status: 'dismissed',
      updatedAt: serverTimestamp(),
    })

    console.log('Alert dismissed successfully')
    return true
  } catch (error) {
    console.error('Error dismissing alert:', error)
    throw error
  }
}

/**
 * Delete an alert permanently
 */
export const deleteAlert = async (
  teacherId: string,
  alertId: string,
): Promise<boolean> => {
  try {
    console.log('Deleting alert:', alertId)

    if (!teacherId || !alertId) {
      throw new Error('Teacher ID and Alert ID are required')
    }

    const alertRef = doc(db, 'teachers', teacherId, 'alerts', alertId)
    await deleteDoc(alertRef)

    console.log('Alert deleted successfully')
    return true
  } catch (error) {
    console.error('Error deleting alert:', error)
    throw error
  }
}

/**
 * Get alerts for a specific student
 */
export const getAlertsByStudent = async (
  teacherId: string,
  studentId: string,
): Promise<Alert[]> => {
  try {
    console.log('Fetching alerts for student:', studentId)

    if (!teacherId || !studentId) {
      throw new Error('Teacher ID and Student ID are required')
    }

    const alertsRef = collection(db, 'teachers', teacherId, 'alerts')
    const alertsQuery = query(
      alertsRef,
      where('studentId', '==', studentId),
      orderBy('createdAt', 'desc'),
    )

    const alertsSnapshot = await getDocs(alertsQuery)

    const alerts: Alert[] = alertsSnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        }) as Alert,
    )

    console.log(`Found ${alerts.length} alerts for student`)
    return alerts
  } catch (error) {
    console.error('Error fetching student alerts:', error)
    throw error
  }
}

/**
 * Update an existing alert
 */
export const updateAlert = async (
  teacherId: string,
  alertId: string,
  updates: Partial<AlertData>,
): Promise<boolean> => {
  try {
    console.log('Updating alert:', alertId, updates)

    if (!teacherId || !alertId) {
      throw new Error('Teacher ID and Alert ID are required')
    }

    const alertRef = doc(db, 'teachers', teacherId, 'alerts', alertId)

    await updateDoc(alertRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    })

    console.log('Alert updated successfully')
    return true
  } catch (error) {
    console.error('Error updating alert:', error)
    throw error
  }
}
